import { LitElement, html, css, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { LayoutAwareMixin } from "@ael/layout-constants";

const TRANSLATIONS = {
    fr: {
        contact: "Contact",
        accessibility: "Accessibilité",
        privacy: "Politique de confidentialité",
        terms: "Conditions générales",
        sitemapLabel: "Plan du site",
        footerLinksLabel: "Liens de bas de page",
    },
    en: {
        contact: "Contact",
        accessibility: "Accessibility",
        privacy: "Privacy Policy",
        terms: "Terms and Conditions",
        sitemapLabel: "Site map",
        footerLinksLabel: "Footer links",
    },
    es: {
        contact: "Contacto",
        accessibility: "Accesibilidad",
        privacy: "Política de privacidad",
        terms: "Condiciones generales",
        sitemapLabel: "Mapa del sitio",
        footerLinksLabel: "Enlaces de pie de página",
    },
    pt: {
        contact: "Contacto",
        accessibility: "Acessibilidade",
        privacy: "Política de privacidade",
        terms: "Termos e condições",
        sitemapLabel: "Mapa do site",
        footerLinksLabel: "Ligações de rodapé",
    },
};

/**
 * Pied de page des sites de l'État de Genève.
 *
 * @fires ge-footer-image-click - Clic sur les armoiries du footer. detail: { originalEvent: MouseEvent }
 */
@customElement("ge-footer")
class GeFooter extends LayoutAwareMixin(LitElement) {
    static styles = css`
        :host {
            display: block;
            grid-area: footer;
            font-family: 'Roboto', Arial, Helvetica, sans-serif;
        }

        footer {
            background: var(--md-sys-color-surface);
            border-top: 1px solid var(--md-sys-color-outline-variant);
        }

        #ge-footer {
            transition: max-width 0.3s ease;
            display: flex;
            justify-content: space-between;
            align-items: center;
            height: 74px;
            padding: 0 32px;
            margin: auto;
        }

        #ge-footer hr {
            border-color: var(--md-sys-color-outline-variant);
            border-style: solid;
            border-bottom: none;
            border-width: 1px;
            width: calc(100% - 16px);
            margin: 4px 0 8px 0;
            display: none;
        }

        #ge-footer > nav {
            display: flex;
            align-items: center;
            gap: 16px;
            margin-right: 20px;
        }

        #ge-footer > nav > a {
            color: var(--md-sys-color-on-surface-variant);
            font-family: 'Roboto', var(--md-ref-typeface-brand), Arial, Helvetica, sans-serif;
            font-size: var(--md-sys-typescale-body-medium-size, 14px);
            font-style: normal;
            line-height: var(--md-sys-typescale-body-medium-line-height, 20px);
            letter-spacing: var(--md-sys-typescale-body-medium-tracking, 0.25px);
            font-weight: 400;
            text-decoration: none;
        }

        #ge-footer > nav > span {
            color: var(--md-sys-color-outline-variant);
        }

        #ge-footer > nav > a:hover {
            color: var(--md-sys-color-primary);
        }

        #ge-footer > nav > a:focus-visible {
            outline: 2px solid var(--md-sys-color-primary);
            outline-offset: 2px;
            border-radius: 2px;
        }

        #ge-footer-armoiries {
            cursor: pointer;
        }

        @media (max-width: 768px) {
            #ge-footer {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: var(--md-ref-spacings-8, 32px);
                height: 278px;
                padding: var(--md-ref-spacings-8, 32px) var(--md-ref-spacings-4, 16px);
            }

            #ge-footer > nav {
                flex-direction: column;
                align-items: flex-start;
                gap: var(--md-ref-spacings-5, 20px);
                margin-right: 0;
            }

            #ge-footer > nav > span {
                display: none;
            }

            #ge-footer-armoiries {
                margin-top: 0;
            }

            #ge-footer hr {
                display: block;
            }
        }

        #ge-footer.maxwidth-formulaire {
            max-width: 1107px;
        }

        #ge-footer.maxwidth-full {
            max-width: 100%;
        }
        
        #ge-footer.maxwidth-from-layout {
            max-width: var(--footer-max-width);
        }

        /* Screen-reader-only: keeps content in the accessibility tree while
           removing it visually (clip technique, not display:none). */
        .visually-hidden {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border: 0;
        }
    `;

    constructor() {
        super();
        // SWC transpiles class fields into instance properties that shadow
        // Lit's reactive prototype accessors. Delete them so Lit's setters work.
        const reactiveProps = [
            'maxWidth', 'fullWidth', 'contactLink', 'accessibilityLink',
            'privacyLink', 'termsLink', 'links', 'sitemap',
        ];
        for (const prop of reactiveProps) {
            if (Object.prototype.hasOwnProperty.call(this, prop)) {
                const val = this[prop];
                delete this[prop];
                this[prop] = val;
            }
        }
    }

    /**
     * @deprecated Use `fullWidth` instead.
     * @type {"true"|"false"}
     */
    @property({ type: String, attribute: 'maxwidth' }) maxWidth = "true";

    /**
     * When `"true"`, footer spans full width. When `"false"`, constrained to 1107px.
     * @type {"true"|"false"}
     */
    @property({ type: String, attribute: 'fullwidth' }) fullWidth = "true";

    /**
     * Effective full-width state. The new `fullWidth` boolean takes precedence,
     * but legacy `maxWidth="false"` still works for backward compat.
     */
    get _isFullWidth() {
        if (this.maxWidth === "false") return false;
        return this.fullWidth === "true";
    }

    /** URL for the Contact link */
    @property({ type: String }) contactLink = "https://www.ge.ch/c/footer-edm-aide";

    /** URL for the Accessibility link */
    @property({ type: String }) accessibilityLink = "https://www.ge.ch/c/footer-edm-accessibilite";

    /** URL for the Privacy Policy link */
    @property({ type: String }) privacyLink = "https://www.ge.ch/c/footer-edm-confidentialite";

    /** URL for the Terms link */
    @property({ type: String }) termsLink = "https://www.ge.ch/c/footer-edm-cgu";

    /**
     * Custom links array. Overrides default links when provided.
     * Each entry accepts `{ title, href }` plus an optional `id`, exposed as
     * `data-ge-track` so analytics can identify the link independently of its URL.
     */
    @property({
        attribute: "links",
        converter: {
            fromAttribute: (value) => {
                if (!value) return undefined;
                try {
                    return JSON.parse(value);
                } catch {
                    return undefined;
                }
            }
        }
    })
    links;

    /**
     * Optional site map ("plan du site"). When provided, renders a
     * screen-reader-only navigation landmark (visually hidden, exposed to
     * assistive tech) so users can navigate the whole site structure from the
     * footer. Each section is an `<h2>` heading followed by a nested list of
     * links; nodes may nest via `children`. The footer's visible appearance is
     * unchanged. Accepts a JSON string attribute or an array property.
     * @type {import("./ge-footer").FooterSitemapSection[]}
     */
    @property({
        attribute: "sitemap",
        converter: {
            fromAttribute: (value) => {
                if (!value) return undefined;
                try {
                    return JSON.parse(value);
                } catch {
                    return undefined;
                }
            }
        }
    })
    sitemap;

    /** Override language for footer labels. If not set, inherits from nearest ancestor [lang] attribute, defaults to 'fr'. */
    @property({ type: String, attribute: 'locale' })
    get locale() {
        return this.#localeValue;
    }
    set locale(value) {
        const oldValue = this.#localeValue;
        this.#localeValue = value || undefined;
        this.requestUpdate('locale', oldValue);
    }

    #localeValue;

    #langObserver;

    get #resolvedLang() {
        if (this.locale) return this.locale;
        const lang = this.closest('[lang]')?.lang;
        return lang ? lang.split('-')[0] : 'fr';
    }

    /** Theme: 'light' or 'dark'. Defaults to system preference, attribute overrides. */
    @property({ type: String, reflect: true, attribute: 'theme' })
    get theme() {
        return this.#resolvedTheme;
    }
    set theme(value) {
        const oldValue = this.#resolvedTheme;
        if (value) {
            this.#resolvedTheme = value;
            this.#userSetTheme = true;
        } else {
            this.#userSetTheme = false;
            this.#resolvedTheme = this.#systemTheme;
        }
        this.requestUpdate('theme', oldValue);
    }

    #resolvedTheme = 'light';
    #systemTheme = 'light';
    #mediaQuery;
    #userSetTheme = false;

    #onSystemThemeChange = () => {
        this.#systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
        if (!this.#userSetTheme) {
            const oldValue = this.#resolvedTheme;
            this.#resolvedTheme = this.#systemTheme;
            this.requestUpdate('theme', oldValue);
        }
    };

    updated(changed) {
        super.updated(changed);
        if (changed.has('maxWidth') && this.maxWidth !== 'true') {
            console.warn(
                'ge-footer: maxwidth attribute is deprecated. Use the fullWidth boolean property instead. ' +
                'Example: <ge-footer .fullWidth=${false}>'
            );
        }
    }

    connectedCallback() {
        super.connectedCallback();

        this.#systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";

        if (!this.hasAttribute("theme")) {
            this.#resolvedTheme = this.#systemTheme;
            this.#userSetTheme = false;
        } else {
            this.#userSetTheme = true;
        }

        this.#mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        this.#mediaQuery.addEventListener("change", this.#onSystemThemeChange);

        this.#langObserver = new MutationObserver(() => {
            if (!this.locale) {
                this.requestUpdate();
            }
        });
        this.#langObserver.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['lang'],
        });
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#mediaQuery?.removeEventListener("change", this.#onSystemThemeChange);
        this.#langObserver?.disconnect();
    }

    get #resolvedLinks() {
        if (this.links?.length) {
            return this.links;
        }
        const t = TRANSLATIONS[this.#resolvedLang] || TRANSLATIONS.fr;
        // `id` identifie le lien indépendamment de son URL (configurable par
        // application) et de sa langue — voir `data-ge-track` dans render().
        return [
            { id: "footer:contact", title: t.contact, href: this.contactLink },
            { id: "footer:accessibility", title: t.accessibility, href: this.accessibilityLink },
            { id: "footer:privacy", title: t.privacy, href: this.privacyLink },
            { id: "footer:terms", title: t.terms, href: this.termsLink },
        ];
    }

    render() {

        const containerClasses = this.getMaxWidthClasses("footer")
        const t = TRANSLATIONS[this.#resolvedLang] || TRANSLATIONS.fr;
        const hasSitemap = Array.isArray(this.sitemap) && this.sitemap.length > 0;

        return html`
            <footer>
                ${hasSitemap ? html`
                    <nav class="visually-hidden" aria-label="${t.sitemapLabel}">
                        ${this.sitemap.map((section) => html`
                            <h2>${section.title}</h2>
                            <ul>
                                ${(section.links ?? []).map((node) => this.#renderSitemapNode(node))}
                            </ul>
                        `)}
                    </nav>
                ` : nothing}
                <div id="ge-footer" class=${classMap(containerClasses)}>
                    <nav aria-label="${t.footerLinksLabel}">
                        ${this.#resolvedLinks.map((link, index) => html`
                            ${index > 0 ? html`<span aria-hidden="true">|</span>` : null}
                            <a
                                href="${link.href}"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-ge-track=${ifDefined(link.id)}
                            >${link.title}</a>
                        `)}
                    </nav>
                    <img
                            id="ge-footer-armoiries"
                            src="https://static.app.ge.ch/theme/icons/common/footer/footer-armoiries-${this.theme}.svg"
                            alt="Armoiries de la République et canton de Genève"
                            height="54"
                            @click="${this.#onImageClick}"
                    />
                </div>
            </footer>
        `;
    }

    /**
     * Renders one site-map node as an `<li>`: a link (or a plain `<span>`
     * label when the node has no `href`) plus a nested `<ul>` for its children.
     * Links are removed from the Tab order (`tabindex="-1"`) since the whole
     * landmark is visually hidden — screen readers still reach them, but
     * sighted keyboard users never focus an invisible element.
     */
    #renderSitemapNode(node) {
        const hasChildren = Array.isArray(node.children) && node.children.length > 0;
        const link = node.href
            ? html`<a
                    href="${node.href}"
                    tabindex="-1"
                    target=${ifDefined(node.target)}
                    rel=${node.target === "_blank" ? "noopener noreferrer" : nothing}
                >${node.title}</a>`
            : html`<span>${node.title}</span>`;
        return html`
            <li>
                ${link}
                ${hasChildren
                ? html`<ul>${node.children.map((child) => this.#renderSitemapNode(child))}</ul>`
                : nothing}
            </li>
        `;
    }

    #onImageClick(e) {
        this.dispatchEvent(new CustomEvent("ge-footer-image-click", {
            detail: { originalEvent: e },
            bubbles: true,
            composed: true
        }));
    }
}

export { GeFooter };